import React, { memo } from 'react';
import { spellKey } from '../../engine/constants';

/**
 * Static / background SVG pieces of the brawl stage.
 * World coordinates (§11.1): x → right, y ↑ up from the floor; the stage draws at svgY = H − y,
 * so the floor is the line svgY = H. Backdrops extend far beyond [0, W] × [0, H] so that a
 * letterboxed / taller viewport shows more of the scene instead of empty bars.
 */

const EXT = 3; // how many world sizes the backdrop extends sideways / upward

export const StageDefs = memo(function StageDefs() {
  return (
    <defs>
      <radialGradient id="brawl-rage">
        <stop offset="0%" stopColor="rgba(255,140,40,0)" />
        <stop offset="65%" stopColor="rgba(255,120,30,0.3)" />
        <stop offset="100%" stopColor="rgba(255,60,20,0)" />
      </radialGradient>
      <radialGradient id="brawl-cast">
        <stop offset="0%" stopColor="rgba(210,170,255,0.85)" />
        <stop offset="100%" stopColor="rgba(150,80,255,0)" />
      </radialGradient>
      <radialGradient id="brawl-glow">
        <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </radialGradient>
      <radialGradient id="brawl-boom">
        <stop offset="0%" stopColor="#fff7c2" />
        <stop offset="35%" stopColor="#ffd84a" />
        <stop offset="70%" stopColor="#ff6a1f" />
        <stop offset="100%" stopColor="rgba(255,60,20,0)" />
      </radialGradient>
      <radialGradient id="brawl-moon">
        <stop offset="0%" stopColor="#fff6e0" />
        <stop offset="45%" stopColor="#ffd9f2" />
        <stop offset="100%" stopColor="rgba(255,120,220,0)" />
      </radialGradient>
      <radialGradient id="brawl-vignette" cx="50%" cy="50%" r="75%">
        <stop offset="55%" stopColor="rgba(0,0,0,0)" />
        <stop offset="100%" stopColor="rgba(0,0,0,0.6)" />
      </radialGradient>
      <radialGradient id="brawl-ledge-glow" cx="50%" cy="0%" r="70%">
        <stop offset="0%" stopColor="rgba(151,71,255,0.55)" />
        <stop offset="100%" stopColor="rgba(151,71,255,0)" />
      </radialGradient>
      <linearGradient id="brawl-sky-arena" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#05020f" />
        <stop offset="45%" stopColor="#150a3a" />
        <stop offset="80%" stopColor="#3b1a7a" />
        <stop offset="100%" stopColor="#8a3dd6" />
      </linearGradient>
      <linearGradient id="brawl-sky-comix" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffb547" />
        <stop offset="100%" stopColor="#ff6a3d" />
      </linearGradient>
      <linearGradient id="brawl-floor-arena" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#2a1650" />
        <stop offset="30%" stopColor="#140a2e" />
        <stop offset="100%" stopColor="#05020c" />
      </linearGradient>
      <linearGradient id="brawl-fog" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(151,71,255,0)" />
        <stop offset="100%" stopColor="rgba(151,71,255,0.28)" />
      </linearGradient>
      <linearGradient id="brawl-ledge-top" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#b9b4d6" />
        <stop offset="100%" stopColor="#5d5784" />
      </linearGradient>
      <linearGradient id="brawl-ledge-body" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#3a3160" />
        <stop offset="100%" stopColor="#1a1433" />
      </linearGradient>
      <linearGradient id="brawl-wall" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#0b0618" />
        <stop offset="100%" stopColor="#1e1240" />
      </linearGradient>
      <linearGradient id="brawl-beam" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(207,255,31,0.16)" />
        <stop offset="100%" stopColor="rgba(207,255,31,0)" />
      </linearGradient>
      <linearGradient id="brawl-trail" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="rgba(255,255,255,0)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0.7)" />
      </linearGradient>
      <pattern id="brawl-halftone" width="12" height="12" patternUnits="userSpaceOnUse">
        <circle cx="6" cy="6" r="2.2" fill="rgba(0,0,0,0.18)" />
      </pattern>
      <pattern id="brawl-bricks" width="60" height="30" patternUnits="userSpaceOnUse">
        <rect width="60" height="30" fill="#7a2f2a" />
        <path d="M0 0H60M0 15H60M30 0V15M0 15V30M60 15V30" stroke="#3a1512" strokeWidth="3" />
      </pattern>
      <pattern id="brawl-plate" width="40" height="40" patternUnits="userSpaceOnUse">
        <rect width="40" height="40" fill="#170d33" />
        <path d="M0 0H40V40" fill="none" stroke="#24164d" strokeWidth="2" />
        <circle cx="5" cy="5" r="1.6" fill="#3b2b70" />
      </pattern>
    </defs>
  );
});

/** Deterministic pseudo-random (stable layout between renders). */
function rand(seed) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function skyline(seed, x0, x1, minH, maxH, minW, maxW) {
  const r = rand(seed);
  const out = [];
  let x = x0;
  while (x < x1) {
    const w = minW + r() * (maxW - minW);
    const h = minH + r() * (maxH - minH);
    out.push({
      x, w, h, antenna: r() > 0.75, lit: Math.floor(r() * 4),
    });
    x += w + r() * 6;
  }
  return out;
}

function ArenaBackdrop({ w, h }) {
  const left = -w * EXT;
  const width = w * (EXT * 2 + 1);
  const top = -h * EXT;
  const stars = rand(7);
  const far = skyline(11, left, left + width, h * 0.22, h * 0.48, w * 0.06, w * 0.13);
  const near = skyline(23, left, left + width, h * 0.1, h * 0.26, w * 0.08, w * 0.16);
  return (
    <g className="brawl-bg brawl-bg--arena">
      <rect x={left} y={top} width={width} height={h - top} fill="url(#brawl-sky-arena)" />
      <rect x={left} y={top} width={width} height={-top} fill="#05020f" />
      {/* stars */}
      <g fill="#fff">
        {Array.from({ length: 70 }, (_, i) => {
          const x = left + stars() * width;
          const y = -h * 0.6 + stars() * h * 1.1;
          const r = 0.8 + stars() * 1.8;
          return <circle key={i} cx={x} cy={y} r={r} opacity={0.25 + stars() * 0.6} className={i % 7 === 0 ? 'brawl-star' : undefined} />;
        })}
      </g>
      {/* moon */}
      <circle cx={w * 0.78} cy={h * 0.14} r={w * 0.22} fill="url(#brawl-moon)" opacity="0.55" />
      <circle cx={w * 0.78} cy={h * 0.14} r={w * 0.075} fill="#ffeef8" opacity="0.9" />
      {/* far city */}
      <g fill="#1d0f45" opacity="0.9">
        {far.map((b) => (
          <g key={`f${b.x}`}>
            <rect x={b.x} y={h - b.h} width={b.w} height={b.h} />
            {b.antenna && <rect x={b.x + b.w / 2 - 1.5} y={h - b.h - 30} width="3" height="30" />}
            {b.antenna && <circle cx={b.x + b.w / 2} cy={h - b.h - 31} r="3" fill="#ff4fa3" className="brawl-beacon" />}
          </g>
        ))}
      </g>
      {/* near city with windows */}
      <g>
        {near.map((b) => (
          <g key={`n${b.x}`}>
            <rect x={b.x} y={h - b.h} width={b.w} height={b.h} fill="#0d0723" />
            {Array.from({ length: b.lit }, (_, r) => (
              <rect key={r} x={b.x + 6} y={h - b.h + 12 + r * 22} width={b.w - 12} height="5" fill="rgba(151,71,255,0.4)" />
            ))}
          </g>
        ))}
      </g>
      {/* spotlights */}
      <path className="brawl-spot brawl-spot--l" d={`M${w * 0.05} ${-h * 0.2} L${-w * 0.1} ${h} L${w * 0.35} ${h} Z`} fill="url(#brawl-beam)" />
      <path className="brawl-spot brawl-spot--r" d={`M${w * 0.95} ${-h * 0.2} L${w * 0.65} ${h} L${w * 1.1} ${h} Z`} fill="url(#brawl-beam)" />
      {/* floor fog */}
      <rect x={left} y={h * 0.6} width={width} height={h * 0.4} fill="url(#brawl-fog)" />
      {/* arena walls outside the world (letterbox) */}
      <rect x={left} y={top} width={-left} height={h - top} fill="url(#brawl-plate)" opacity="0.92" />
      <rect x={w} y={top} width={-left} height={h - top} fill="url(#brawl-plate)" opacity="0.92" />
      <rect x={-6} y={top} width="6" height={h - top} fill="#cfff1f" opacity="0.35" />
      <rect x={w} y={top} width="6" height={h - top} fill="#cfff1f" opacity="0.35" />
    </g>
  );
}

function ComixBackdrop({ w, h }) {
  const left = -w * EXT;
  const width = w * (EXT * 2 + 1);
  const top = -h * EXT;
  const city = skyline(5, left, left + width, h * 0.3, h * 0.6, 50, 90);
  return (
    <g className="brawl-bg brawl-bg--comix">
      <rect x={left} y={top} width={width} height={h - top} fill="url(#brawl-sky-comix)" />
      <rect x={left} y={top} width={width} height={h - top} fill="url(#brawl-halftone)" />
      {city.map((b) => (
        <rect key={b.x} x={b.x} y={h - b.h} width={b.w} height={b.h} fill="#1a0f1f" />
      ))}
      <rect x={left} y={h * 0.5} width={width} height={h * 0.5} fill="url(#brawl-bricks)" />
      <rect x={left} y={h * 0.5} width={width} height={h * 0.5} fill="url(#brawl-halftone)" />
      <text x={w * 0.12} y={h * 0.72} className="brawl-graffiti" transform={`rotate(-6 ${w * 0.12} ${h * 0.72})`}>DATUM</text>
    </g>
  );
}

export const Backdrop = memo(function Backdrop({ theme, w, h }) {
  return theme === 'comix' ? <ComixBackdrop w={w} h={h} /> : <ArenaBackdrop w={w} h={h} />;
});

/** The floor (svgY = h) and everything below it (the slab under the controls). */
export const Floor = memo(function Floor({ theme, w, h }) {
  const left = -w * EXT;
  const width = w * (EXT * 2 + 1);
  const depth = h * 3;
  if (theme === 'comix') {
    return (
      <g className="brawl-floor brawl-floor--comix">
        <rect x={left} y={h} width={width} height={depth} fill="#3b3346" />
        <path d={`M${left} ${h} H${left + width}`} stroke="#000" strokeWidth="6" />
        <path d={`M${left} ${h + 36} H${left + width}`} stroke="#ffd84a" strokeWidth="5" strokeDasharray="60 40" />
      </g>
    );
  }
  return (
    <g className="brawl-floor brawl-floor--arena">
      <rect x={left} y={h} width={width} height={depth} fill="url(#brawl-floor-arena)" />
      <g stroke="rgba(151,71,255,0.4)" strokeWidth="2">
        {Array.from({ length: 13 }, (_, i) => {
          const x = (w / 12) * i;
          return <line key={i} x1={x} y1={h} x2={w / 2 + (x - w / 2) * 2.2} y2={h + w * 0.6} />;
        })}
        <line x1={left} y1={h + 26} x2={left + width} y2={h + 26} />
        <line x1={left} y1={h + 64} x2={left + width} y2={h + 64} />
      </g>
      <rect x={0} y={h} width={w} height="6" fill="#cfff1f" opacity="0.85" />
      <rect x={0} y={h - 3} width={w} height="3" fill="#fff" opacity="0.5" />
    </g>
  );
});

/** Lighting vignette over the world (non-interactive). */
export const Vignette = memo(function Vignette({ vb }) {
  if (!vb) return null;
  return <rect className="brawl-vignette" x={vb[0]} y={vb[1]} width={vb[2]} height={vb[3]} fill="url(#brawl-vignette)" pointerEvents="none" />;
});

// ── Platforms (one-way ledges) ──────────────────────────────────────────────

const Ledge = memo(function Ledge({
  x1, x2, sy, theme,
}) {
  const wdt = Math.max(10, x2 - x1);
  if (theme === 'comix') {
    return (
      <g className="bledge bledge--comix">
        <rect x={x1} y={sy} width={wdt} height="22" fill="url(#brawl-bricks)" stroke="#000" strokeWidth="4" />
      </g>
    );
  }
  const rivets = Math.max(2, Math.floor(wdt / 30));
  return (
    <g className="bledge" data-ledge={`${x1}-${x2}`}>
      {/* under-glow */}
      <ellipse cx={x1 + wdt / 2} cy={sy + 30} rx={wdt * 0.6} ry="34" fill="url(#brawl-ledge-glow)" />
      {/* brackets */}
      <path d={`M${x1 + 14} ${sy + 26} l12 26 l12 -26 Z M${x2 - 38} ${sy + 26} l12 26 l12 -26 Z`} fill="#1a1433" stroke="#000" strokeWidth="2" />
      {/* body */}
      <rect x={x1 + 3} y={sy + 8} width={wdt - 6} height="20" rx="4" fill="url(#brawl-ledge-body)" stroke="#000" strokeWidth="2.5" />
      {Array.from({ length: rivets }, (_, i) => (
        <circle key={i} cx={x1 + ((i + 0.5) * wdt) / rivets} cy={sy + 19} r="2.6" fill="#7a6fb0" stroke="#000" strokeWidth="1" />
      ))}
      {/* top plate */}
      <rect x={x1} y={sy} width={wdt} height="10" rx="3" fill="url(#brawl-ledge-top)" stroke="#000" strokeWidth="2.5" />
      {/* glowing edge */}
      <rect x={x1 + 4} y={sy - 2} width={wdt - 8} height="4" rx="2" fill="#cfff1f" opacity="0.35" className="bledge__glow" />
      <rect x={x1 + 6} y={sy - 0.5} width={wdt - 12} height="1.6" rx="1" fill="#f3ffc2" />
    </g>
  );
});

export const Platforms = memo(function Platforms({ platforms, h, theme }) {
  if (!platforms || !platforms.length) return null;
  return (
    <g className="brawl-layer brawl-layer--platforms">
      {platforms.map((p) => (
        <Ledge key={`${p.x1}-${p.x2}-${p.y}`} x1={p.x1} x2={p.x2} sy={h - p.y} theme={theme} />
      ))}
    </g>
  );
});

// ── Projectiles ─────────────────────────────────────────────────────────────

const PROJECTILE_SHAPES = {
  batarang: (
    <g className="bp-spin">
      <path d="M-26 0 Q-14 -10 -8 -3 Q-4 -12 0 -5 Q4 -12 8 -3 Q14 -10 26 0 Q14 4 8 10 Q4 4 0 8 Q-4 4 -8 10 Q-14 4 -26 0Z" fill="#1b1b1f" stroke="#ffd84a" strokeWidth="2.5" />
    </g>
  ),
  shield: (
    <g className="bp-spin">
      <circle r="24" fill="#e63946" stroke="#000" strokeWidth="3" />
      <circle r="17" fill="#f4f4f4" />
      <circle r="11" fill="#2c5ad6" />
      <path d="M0 -8 L2.4 -2.4 L8 -2.4 L3.6 1.2 L5.2 7 L0 3.6 L-5.2 7 L-3.6 1.2 L-8 -2.4 L-2.4 -2.4Z" fill="#fff" />
    </g>
  ),
  web: (
    <g className="bp-spin">
      <circle r="20" fill="rgba(255,255,255,0.18)" stroke="#fff" strokeWidth="2" />
      <path d="M-20 0H20M0 -20V20M-14 -14L14 14M14 -14L-14 14" stroke="#fff" strokeWidth="1.6" />
      <circle r="13" fill="none" stroke="#fff" strokeWidth="1.3" />
      <circle r="6.5" fill="none" stroke="#fff" strokeWidth="1.2" />
    </g>
  ),
  blaster: (
    <g>
      <ellipse rx="38" ry="11" fill="rgba(255,60,60,0.35)" />
      <ellipse rx="28" ry="5.5" fill="none" stroke="#ff4d4d" strokeWidth="3" />
      <ellipse rx="24" ry="3.5" fill="#fff" />
    </g>
  ),
  banana: (
    <g className="bp-spin">
      <path d="M-14 -6 Q0 18 16 -4 Q2 8 -14 -6Z" fill="#ffe14a" stroke="#000" strokeWidth="2.5" />
    </g>
  ),
  dive: (
    <g>
      <path d="M-22 0 L10 -14 L26 0 L10 14Z" fill="#ffb547" stroke="#000" strokeWidth="2.5" />
    </g>
  ),
  orb: (
    <g>
      <circle r="24" fill="url(#brawl-cast)" />
      <circle r="10" fill="#e6d3ff" />
    </g>
  ),
};

const TRAIL_COLOR = {
  batarang: '#ffd84a', shield: '#7ab0ff', web: '#ffffff', blaster: '#ff4d4d', banana: '#ffe14a', dive: '#ffb547', orb: '#b36bff',
};

/** Projectile body (position is applied by the stage through a ref). */
export const ProjectileShape = memo(function ProjectileShape({ k, ownerSpell, d }) {
  const key = spellKey(k, ownerSpell);
  const shape = PROJECTILE_SHAPES[key] || PROJECTILE_SHAPES.orb;
  const c = TRAIL_COLOR[key] || '#fff';
  return (
    <g transform={`scale(${d < 0 ? -1 : 1} 1)`} className={`bp bp--${key}`}>
      <path d="M-96 0 L-18 -9 L-18 9Z" fill={c} opacity="0.28" className="bp-trail" />
      <path d="M-70 -3 L-18 -3 M-82 4 L-24 4" stroke={c} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      {shape}
    </g>
  );
});

// ── Pickups / traps / barrels ───────────────────────────────────────────────

const ITEM_STYLE = {
  HEALTH: { color: '#5ee67a', glyph: <path d="M-4 -11H4V-4H11V4H4V11H-4V4H-11V-4H-4Z" fill="#fff" /> },
  MANA: { color: '#3a9bff', glyph: <path d="M0 -12 Q9 0 7 5 A7 7 0 0 1 -7 5 Q-9 0 0 -12Z" fill="#fff" /> },
  RAGE: { color: '#ff5a2a', glyph: <path d="M-2 -13 Q8 -4 4 2 Q10 -1 9 -6 Q16 6 4 12 H-4 Q-14 6 -7 -4 Q-6 2 -2 2 Q-6 -6 -2 -13Z" fill="#fff" /> },
};

/** Pickup drawn around its centre; the stage places it at (x, H − y − 20) through a ref. */
export const PickupShape = memo(function PickupShape({ k }) {
  const s = ITEM_STYLE[k] || ITEM_STYLE.MANA;
  return (
    <g className={`bpick bpick--${String(k).toLowerCase()}`} data-pickup={k}>
      <g className="bpick__bob">
        <circle r="32" fill={s.color} opacity="0.25" className="bpick__glow" />
        <circle r="18" fill={s.color} stroke="#000" strokeWidth="3" />
        <circle r="18" fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.5" strokeDasharray="10 40" className="bpick__shine" />
        {s.glyph}
      </g>
    </g>
  );
});

export const Trap = memo(function Trap({ x, sy }) {
  return (
    <g transform={`translate(${x} ${sy - 6})`} className="btrap">
      <path d="M-20 4 Q-8 -14 0 -4 Q8 -16 22 4 Q8 -4 0 6 Q-8 -4 -20 4Z" fill="#ffe14a" stroke="#000" strokeWidth="2.5" />
      <path d="M-2 -4 L0 -12" stroke="#5a3d1d" strokeWidth="3" />
    </g>
  );
});

export const Barrel = memo(function Barrel({
  x, sy, alive, fuse,
}) {
  if (!alive) {
    return (
      <g transform={`translate(${x} ${sy})`} className="bbarrel bbarrel--broken">
        <path d="M-26 0 L-18 -14 L-8 -6 L0 -18 L10 -8 L20 -16 L28 0Z" fill="#3b2516" stroke="#000" strokeWidth="2.5" />
      </g>
    );
  }
  return (
    <g transform={`translate(${x} ${sy})`} className={`bbarrel${fuse ? ' bbarrel--fuse' : ''}`}>
      <g className="bbarrel__body">
        <rect x="-26" y="-70" width="52" height="70" rx="10" fill="#b2342a" stroke="#000" strokeWidth="3.5" />
        <rect x="-26" y="-56" width="52" height="7" fill="#2a2a2a" />
        <rect x="-26" y="-20" width="52" height="7" fill="#2a2a2a" />
        <path d="M0 -44 L10 -26 H-10Z" fill="#ffd84a" stroke="#000" strokeWidth="2" />
        <text x="0" y="-29" fontSize="11" fontWeight="900" textAnchor="middle">!</text>
      </g>
      {fuse && <circle className="bbarrel__spark" cx="12" cy="-76" r="7" fill="#ffd84a" />}
    </g>
  );
});
